import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Brand, Button } from '@aigovops/design-system';

/**
 * Shared shell for `/samples/*` marketing sample pages.
 *
 * Public read-only surface. Announces the sample nature, provides
 * consistent chrome (header, sample banner, footer CTA), and passes an
 * accent color to the child page (purple for Buying, teal for Building).
 *
 * No workspace context. No auth. Uses static seed data only —
 * see docs/v4/link-gaps-plan.md Path B.
 */

export type SampleAccent = 'buying' | 'building';

const accentVar = (a: SampleAccent): string =>
  a === 'buying' ? 'var(--buying)' : 'var(--building)';

const accentSoftVar = (a: SampleAccent): string =>
  a === 'buying' ? 'var(--buying-soft)' : 'var(--building-soft)';

export interface SampleShellProps {
  /** Which lane this sample belongs to — drives accent color. */
  accent: SampleAccent;
  /** Small mono uppercase label above the H1 (e.g. "SAMPLE · SUPPLIER AI REGISTER"). */
  eyebrow: string;
  /** Big page title. */
  title: string;
  /** One-sentence subhead framing what the visitor is looking at. */
  subtitle: string;
  /** Main content — the sample UI. */
  children: ReactNode;
  /** Optional link back to the paired-lane landing (e.g. `/buying` or `/building`). */
  laneHref?: string;
  /** Optional label for the paired-lane link. */
  laneLabel?: string;
  /** Copy for the cross-lane switch link at the bottom of the page. */
  crossLane?: {
    href: string;
    label: string;
  };
  /**
   * Destination for the brand glyph/wordmark and the "Home" nav link.
   * Both sample pages now live under `/buying/samples/*` (V85-2-7); default
   * to `/buying` so the shell never emits a same-origin link that resolves
   * outside the `/buying` lane (see `buying-sealed.spec.ts`).
   */
  homeHref?: string;
}

export function SampleShell({
  accent,
  eyebrow,
  title,
  subtitle,
  children,
  laneHref,
  laneLabel,
  crossLane,
  homeHref = '/buying',
}: SampleShellProps) {
  const accentColor = accentVar(accent);
  const accentSoft = accentSoftVar(accent);

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100dvh' }}>
      <header
        style={{
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface)',
        }}
      >
        <div
          style={{
            maxWidth: '80rem',
            margin: '0 auto',
            padding: 'var(--space-4) var(--space-6)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-4)',
          }}
        >
          <Link
            to={homeHref}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              textDecoration: 'none',
              minWidth: 0,
            }}
          >
            <Brand variant="glyph" />
            <Brand variant="wordmark" />
          </Link>
          <nav
            style={{
              flex: 1,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 'var(--space-4)',
              fontSize: 'var(--font-size-2xs)',
              fontFamily: 'var(--font-ui)',
            }}
          >
            {laneHref && laneLabel ? (
              <Link
                to={laneHref}
                style={{
                  color: 'var(--text-muted)',
                  textDecoration: 'none',
                }}
              >
                {laneLabel} →
              </Link>
            ) : null}
            <Link to={homeHref} style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>
              Home
            </Link>
            <Button
              variant="primary"
              compact
              onClick={() =>
                window.open(
                  '/demo',
                  '_blank',
                  'noopener,noreferrer',
                )
              }
            >
              Book a demo
            </Button>
          </nav>
        </div>
      </header>

      {/* Sample banner — makes it unmistakable this is a preview, not a customer instance. */}
      <div
        role="note"
        style={{
          background: accentSoft,
          borderBottom: `1px solid ${accentColor}`,
          padding: 'var(--space-3) var(--space-6)',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--font-size-3xs)',
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          color: accentColor,
          fontWeight: 700,
          textAlign: 'center',
        }}
      >
        SAMPLE — READ-ONLY EXPLORATION · Click any row to see details · All companies, executives, and identifiers are fictional
      </div>

      {/* Page hero */}
      <section
        style={{
          maxWidth: '80rem',
          margin: '0 auto',
          paddingTop: 'var(--space-10)',
          paddingRight: 'var(--space-6)',
          paddingBottom: 'var(--space-6)',
          paddingLeft: 'var(--space-6)',
          display: 'grid',
          gap: 'var(--space-3)',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--font-size-3xs)',
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            color: accentColor,
            fontWeight: 700,
          }}
        >
          {eyebrow}
        </div>
        <h1
          style={{
            margin: 0,
            fontFamily: 'var(--font-ui)',
            fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
            lineHeight: 1.15,
            fontWeight: 700,
            color: 'var(--text)',
          }}
        >
          {title}
        </h1>
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-md)',
            color: 'var(--text-muted)',
            lineHeight: 'var(--line-height-body)',
            maxWidth: '52rem',
          }}
        >
          {subtitle}
        </p>
      </section>

      {/* Main content — the sample UI. */}
      <main
        style={{
          maxWidth: '80rem',
          margin: '0 auto',
          paddingTop: 'var(--space-6)',
          paddingRight: 'var(--space-6)',
          paddingBottom: 'var(--space-16)',
          paddingLeft: 'var(--space-6)',
        }}
      >
        {children}
      </main>

      {/* Bottom CTA band — no divider; 104px vertical rhythm carries the break. */}
      <section
        style={{
          maxWidth: '80rem',
          margin: '0 auto',
          paddingTop: 'var(--space-16)',
          paddingRight: 'var(--space-6)',
          paddingBottom: 'var(--space-10)',
          paddingLeft: 'var(--space-6)',
          display: 'grid',
          gap: 'var(--space-3)',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontFamily: 'var(--font-ui)',
            fontSize: 'clamp(1.5rem, 2.8vw, 1.875rem)',
            fontWeight: 700,
            color: 'var(--text)',
          }}
        >
          See this with your own data.
        </h2>
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-muted)',
            maxWidth: '52rem',
          }}
        >
          A 20-minute walkthrough with a named editor. We'll show you the register wired to your
          own supplier list or AI systems — no procurement paperwork required to see it.
        </p>
        <div style={{ marginTop: 'var(--space-2)' }}>
          <Button
            variant="primary"
            onClick={() =>
              window.open(
                '/demo',
                '_blank',
                'noopener,noreferrer',
              )
            }
          >
            Book a 20-min walkthrough →
          </Button>
        </div>
        {crossLane ? (
          <div style={{ marginTop: 'var(--space-3)' }}>
            <Link
              to={crossLane.href}
              style={{
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
              }}
            >
              {crossLane.label} →
            </Link>
          </div>
        ) : null}
      </section>

      <footer
        style={{
          borderTop: '1px solid var(--border)',
          padding: 'var(--space-6)',
          background: 'var(--surface)',
        }}
      >
        <div
          style={{
            maxWidth: '80rem',
            margin: '0 auto',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--text-dim)',
          }}
        >
          © 2026 gacpro · gacpro.io · Sample data — no real supplier or system referenced.
        </div>
      </footer>
    </div>
  );
}

/** Chip used to render a disposition, coloured neutrally per Elena's Blocker fix
 *  (no red/green admission chrome — lifecycle status only). Also used in the
 *  supplier and building sample pages. */
export function DispositionChip({
  label,
  tone,
}: {
  label: string;
  tone: 'systemic' | 'pending' | 'awaiting' | 'outside' | 'retired';
}) {
  const toneColor: Record<typeof tone, { bg: string; fg: string }> = {
    systemic: { bg: 'var(--band-green-bg)', fg: 'var(--band-green)' },
    pending: { bg: 'var(--band-amber-bg)', fg: 'var(--band-amber)' },
    awaiting: { bg: 'var(--band-blue-bg)', fg: 'var(--band-blue)' },
    outside: { bg: 'var(--band-red-bg)', fg: 'var(--band-red)' },
    retired: { bg: 'var(--surface-inset)', fg: 'var(--text-dim)' },
  };
  const t = toneColor[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px var(--space-2)',
        borderRadius: '999px',
        background: t.bg,
        color: t.fg,
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--font-size-3xs)',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}
